/**
 * Multi-site: the site the panel is currently managing. Persisted in localStorage and sent as
 * X-Site-Id on every API call (src/api/http.js). The API validates it against its registry and
 * the admin's allowed sites.
 */
const KEY = 'panel_site_id'
const LIST_KEY = 'panel_sites'
export const DEFAULT_SITE_ID = 'sakhtineo'
export const SAKHTINEO_LOGO = 'https://db.sakhtineo.com/uploads/image/2026/10/mve62o7o-8dd2711ed9018f09.png'
// storefront permalink patterns (mirror of the API registry; used until /api/admin/sites answers)
const PERMALINKS_SAKHTINEO = { home: '/', post: '/blog/{slug}', page: '/page/{slug}', product: '/product/{slug}', category: '/blog/category/{slug}', tag: '/blog/tag/{slug}', product_category: '/shop/cat/{slug}', product_tag: '/shop/tag/{slug}' }
const PERMALINKS_WP = { home: '/', post: '/{slug}/', page: '/{slug}/', product: '/{slug}/', category: '/category/{slug}/', tag: '/tag/{slug}/', product_category: '/category/{slug}/', product_tag: '/tag/{slug}/' }
const FALLBACK = [{ id: 'sakhtineo', label: '\u0633\u0627\u062e\u062a\u06cc\u0646\u0648', name: 'Sakhtineo', url: 'https://sakhtineo.com/', domain: 'sakhtineo.com', logo: SAKHTINEO_LOGO, favicon: 'https://sakhtineo.com/favicon.ico', permalinks: PERMALINKS_SAKHTINEO }]

export function getSiteId() {
  try {
    const v = localStorage.getItem(KEY) || ''
    return /^[a-z][a-z0-9_-]{1,31}$/.test(v) ? v : DEFAULT_SITE_ID
  } catch (e) { return DEFAULT_SITE_ID }
}

export function setSiteId(id) {
  try { localStorage.setItem(KEY, id) } catch (e) { /* ignore */ }
}

export function getSites() {
  try {
    const arr = JSON.parse(localStorage.getItem(LIST_KEY) || '[]')
    return Array.isArray(arr) && arr.length ? arr : FALLBACK
  } catch (e) { return FALLBACK }
}

export function setSites(list) {
  if (!Array.isArray(list)) return
  const clean = list.filter((s) => s && /^[a-z][a-z0-9_-]{1,31}$/.test(s.id)).map((s) => ({ id: s.id, label: s.label || s.name || s.id, name: s.name || s.id, url: s.url || '', domain: s.domain || (Array.isArray(s.domains) && s.domains[0]) || String(s.url || '').replace(/^https?:\/\//, '').replace(/\/.*$/, ''), logo: safeUrl(s.logo), favicon: safeUrl(s.favicon), permalinks: cleanPermalinks(s.permalinks) }))
  try { localStorage.setItem(LIST_KEY, JSON.stringify(clean)) } catch (e) { /* ignore */ }
  // current site no longer allowed -> fall back to the first allowed one
  if (clean.length && !clean.some((s) => s.id === getSiteId())) setSiteId(clean[0].id)
}

export function currentSite() {
  const id = getSiteId()
  return getSites().find((s) => s.id === id) || { id, label: id, name: id }
}

/** Switch site and reload so every view refetches its data for the new site. */
export function switchSite(id) {
  if (!id || id === getSiteId()) return
  setSiteId(id)
  window.location.reload()
}

/** Lower-case + unify Arabic/Persian letter variants so the site search matches either form. */
export function normalizeFa(v) {
  return String(v || '').trim().toLowerCase()
    .replace(/[\u064a\u0649]/g, '\u06cc').replace(/\u0643/g, '\u06a9').replace(/[\u0623\u0625\u0622]/g, '\u0627')
    .replace(/\u0629/g, '\u0647').replace(/[\u064b-\u065f\u200c]/g, '')
}

function safeUrl(v) {
  const u = String(v || '').trim()
  return /^(https:\/\/|\/)[^\s"'<>]*$/.test(u) ? u : ''
}
function cleanPermalinks(p) {
  if (!p || typeof p !== 'object') return null
  const out = {}
  Object.keys(p).forEach((k) => { if (typeof p[k] === 'string' && p[k].charAt(0) === '/') out[k] = p[k] })
  return Object.keys(out).length ? out : null
}

/** Storefront origin of a site (no trailing slash), e.g. https://hellal.ir */
export function siteBase(site = currentSite()) {
  let u = String((site && site.url) || '').trim()
  if (!u && site && site.domain) u = 'https://' + site.domain
  if (!u) { try { u = localStorage.getItem('web_address') || '' } catch (e) { /* ignore */ } }
  return u.replace(/\/+$/, '')
}
function permalinks(site) {
  return (site && site.permalinks) || (site && site.id !== DEFAULT_SITE_ID ? PERMALINKS_WP : PERMALINKS_SAKHTINEO)
}
/** Path of a content item on the site's storefront: type = post|page|product|category|tag|product_category|product_tag|home */
export function sitePath(type, slug, site = currentSite()) {
  const pl = permalinks(site)
  const pat = pl[type] || (type === 'home' ? '/' : '/{slug}')
  return pat.split('{slug}').join(String(slug == null ? '' : slug).replace(/^\/+|\/+$/g, ''))
}
/** Path prefix before the slug, e.g. '/blog/' (sakhtineo posts) or '/' (hellal posts) */
export function sitePrefix(type, site = currentSite()) {
  const pat = permalinks(site)[type] || '/{slug}'
  return pat.split('{slug}')[0]
}
/** Absolute storefront URL of a content item on the active site. */
export function siteLink(type, slug, site = currentSite()) {
  return siteBase(site) + sitePath(type, slug, site)
}
/** Sidebar logo: the site's own registry logo. Only the default site falls back to the Sakhtineo logo;
 *  other sites get '' (no logo) instead of someone else's branding. */
export function siteLogo(site = currentSite()) {
  if (site && site.logo) return site.logo
  if (site && site.id === DEFAULT_SITE_ID) {
    const def = getSites().find((x) => x.id === DEFAULT_SITE_ID)
    return (def && def.logo) || SAKHTINEO_LOGO
  }
  return ''
}

const SITE_ID_RE = /^[a-z][a-z0-9_-]{1,31}$/
function cachedSites() {
  try {
    const arr = JSON.parse(localStorage.getItem(LIST_KEY) || '[]')
    return Array.isArray(arr) ? arr : []
  } catch (e) { return [] }
}
/** Registry entry of a site if this browser has seen it (sites list from a previous login). */
export function knownSite(id) {
  return cachedSites().find((s) => s.id === id) || FALLBACK.find((s) => s.id === id) || null
}
/**
 * Which site a login page belongs to, before the admin is authenticated. In order:
 *   1. ?site=<id> in the login URL (a per-site login link),
 *   2. the panel hostname matching a known site domain (e.g. panel.hellal.ir -> hellal),
 *   3. the site this browser last worked on (panel_site_id, only if it was ever stored).
 * Returns { id, source } or null when nothing identifies the site (fresh browser on a shared host).
 */
export function resolveLoginSite(query) {
  const q = query && query.site
  if (typeof q === 'string' && SITE_ID_RE.test(q)) return { id: q, source: 'query' }
  const host = String((typeof location !== 'undefined' && location.hostname) || '').toLowerCase().replace(/^www\./, '')
  const byHost = cachedSites().find((s) => {
    const d = String(s.domain || '').toLowerCase().replace(/^www\./, '')
    return d && (host === d || host.endsWith('.' + d))
  })
  if (byHost) return { id: byHost.id, source: 'domain' }
  let stored = ''
  try { stored = localStorage.getItem(KEY) || '' } catch (e) { /* ignore */ }
  if (SITE_ID_RE.test(stored)) return { id: stored, source: 'last' }
  return null
}
/** Open a storefront link in a new tab. */
export function openOnSite(type, slug) {
  window.open(siteLink(type, slug), '_blank', 'noopener')
}
